/**
 * Searchable SignalK path catalog (grouped list). Used embedded in widget editor
 * and on the full Explorer page.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import {
  EXPLORER_HIDDEN_CATEGORIES,
  formatExplorerDisplayString,
  formatExplorerReading,
  groupCatalogForExplorer,
  catalogPathSuffix,
  statusColor,
  statusLabel,
} from '../../utils/signalkPathUtils';

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  flex: 1 1 auto;
  gap: 0.75rem;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
`;

const Hint = styled.p`
  margin: 0;
  font-size: ${theme.fontSizes.sm};
  color: ${(p) => (p.$dark ? '#94a3b8' : theme.colors.text.secondary)};
  line-height: 1.45;
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 180px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem 0.5rem 2rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${(p) => (p.$dark ? '#475569' : theme.colors.border.secondary)};
  background: ${(p) => (p.$dark ? '#0f172a' : theme.colors.background.secondary)};
  color: ${(p) => (p.$dark ? '#f8fafc' : theme.colors.text.primary)};
  font-size: ${theme.fontSizes.sm};
`;

const SearchIcon = styled(MagnifyingGlassIcon)`
  position: absolute;
  left: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1rem;
  height: 1rem;
  color: ${(p) => (p.$dark ? '#94a3b8' : theme.colors.text.secondary)};
`;

const Select = styled.select`
  padding: 0.45rem 0.65rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${(p) => (p.$dark ? '#475569' : theme.colors.border.secondary)};
  background: ${(p) => (p.$dark ? '#0f172a' : theme.colors.background.secondary)};
  color: ${(p) => (p.$dark ? '#f8fafc' : theme.colors.text.primary)};
  font-size: ${theme.fontSizes.sm};
`;

const Panel = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid ${(p) => (p.$dark ? '#475569' : theme.colors.border.primary)};
  border-radius: ${theme.borderRadius.lg};
  background: ${(p) => (p.$dark ? '#0f172a' : theme.colors.background.paper)};
  overflow: hidden;
`;

const ListWrap = styled.div`
  overflow: auto;
  flex: 1;
`;

const CategoryBlock = styled.div`
  border-bottom: 1px solid ${(p) => (p.$dark ? '#334155' : theme.colors.border.primary)};
`;

const CategoryHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 0.85rem;
  background: ${(p) => (p.$dark ? '#1e293b' : theme.colors.background.secondary)};
  border: none;
  cursor: pointer;
  text-align: left;
  color: ${(p) => (p.$dark ? '#f1f5f9' : theme.colors.text.primary)};
  font-weight: ${theme.fontWeights.semibold};
  font-size: ${theme.fontSizes.sm};

  &:hover {
    background: ${(p) => (p.$dark ? '#334155' : theme.colors.background.primary)};
  }
`;

const CollectionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.85rem 0.5rem 1.75rem;
  background: ${(p) => (p.$dark ? '#0f172a' : theme.colors.background.paper)};
  border: none;
  border-top: 1px solid ${(p) => (p.$dark ? '#334155' : theme.colors.border.secondary)};
  cursor: pointer;
  text-align: left;
  color: ${(p) => (p.$dark ? '#e2e8f0' : theme.colors.text.primary)};
  font-weight: ${theme.fontWeights.medium};
  font-size: ${theme.fontSizes.sm};

  &:hover {
    background: ${(p) => (p.$dark ? '#1e293b' : theme.colors.background.secondary)};
  }
`;

const PathRow = styled.div`
  display: grid;
  grid-template-columns: ${(p) =>
    p.$pickOne
      ? 'minmax(140px, 1.2fr) minmax(100px, 1fr) 72px 88px auto'
      : '36px minmax(140px, 1.2fr) minmax(100px, 1fr) 72px 88px'};
  gap: 0.5rem;
  align-items: center;
  padding: 0.45rem 0.85rem 0.45rem ${(p) => (p.$pickOne ? '1.75rem' : '2.75rem')};
  border-top: 1px solid ${(p) => (p.$dark ? '#334155' : theme.colors.border.secondary)};
  font-size: ${theme.fontSizes.sm};
  background: ${(p) => (p.$dark ? '#0f172a' : theme.colors.background.primary)};

  &:hover {
    background: ${(p) => (p.$dark ? '#1e293b' : theme.colors.background.secondary)};
  }
`;

const ReadingLabel = styled.div`
  font-weight: ${theme.fontWeights.medium};
  color: ${(p) => (p.$dark ? '#f8fafc' : theme.colors.text.primary)};
`;

const ReadingSub = styled.div`
  font-size: ${theme.fontSizes.xs};
  color: ${(p) => (p.$dark ? '#94a3b8' : theme.colors.text.secondary)};
  font-family: ${theme.typography.fontFamily.mono};
  margin-top: 0.1rem;
  word-break: break-all;
`;

const ValueCell = styled.div`
  font-weight: ${theme.fontWeights.semibold};
  color: ${(p) => (p.$dark ? '#f8fafc' : theme.colors.text.primary)};
  font-variant-numeric: tabular-nums;
`;

const UsePathButton = styled.button`
  padding: 0.3rem 0.65rem;
  border-radius: ${theme.borderRadius.md};
  border: none;
  background: ${theme.colors.primary.main};
  color: white;
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.medium};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${theme.colors.primary.dark};
  }
`;

const Checkbox = styled.input`
  cursor: pointer;
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 0.35rem;
  background: ${(p) => p.$color};
`;

const CountBadge = styled.span`
  margin-left: auto;
  font-size: ${theme.fontSizes.xs};
  color: ${(p) => (p.$dark ? '#94a3b8' : theme.colors.text.secondary)};
`;

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 0.85rem;
  border-top: 1px solid ${(p) => (p.$dark ? '#475569' : theme.colors.border.primary)};
  background: ${(p) => (p.$dark ? '#1e293b' : theme.colors.background.secondary)};
  font-size: ${theme.fontSizes.sm};
  color: ${(p) => (p.$dark ? '#94a3b8' : theme.colors.text.secondary)};
`;

const Btn = styled.button`
  padding: 0.45rem 0.85rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${(p) => (p.$dark ? '#475569' : theme.colors.border.secondary)};
  background: ${(p) => (p.$primary ? theme.colors.primary.main : p.$dark ? '#334155' : theme.colors.background.paper)};
  color: ${(p) => (p.$primary ? '#fff' : p.$dark ? '#f8fafc' : theme.colors.text.primary)};
  font-size: ${theme.fontSizes.sm};
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ColumnHeader = styled.div`
  display: grid;
  grid-template-columns: ${(p) =>
    p.$pickOne
      ? 'minmax(140px, 1.2fr) minmax(100px, 1fr) 72px 88px auto'
      : '36px minmax(140px, 1.2fr) minmax(100px, 1fr) 72px 88px'};
  gap: 0.5rem;
  padding: 0.45rem 0.85rem;
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.medium};
  color: ${(p) => (p.$dark ? '#94a3b8' : theme.colors.text.secondary)};
  background: ${(p) => (p.$dark ? '#1e293b' : theme.colors.background.secondary)};
  border-bottom: 1px solid ${(p) => (p.$dark ? '#475569' : theme.colors.border.primary)};
  position: sticky;
  top: 0;
  z-index: 2;
`;

function formatTs(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleTimeString();
  } catch {
    return String(ts);
  }
}

function selectionState(paths, selected) {
  const keys = paths.map((p) => p.path);
  const selectedCount = keys.filter((k) => selected.has(k)).length;
  return {
    all: keys.length > 0 && selectedCount === keys.length,
    some: selectedCount > 0 && selectedCount < keys.length,
    count: selectedCount,
  };
}

/**
 * @param {object} props
 * @param {number} props.vesselId
 * @param {'single'|'multiple'} [props.selectionMode='single']
 * @param {boolean} [props.dark=false]
 * @param {string[]} [props.selectedPaths=[]]
 * @param {(path: string, row?: object) => void} [props.onPathSelect]
 * @param {React.ReactNode} [props.footerExtra]
 * @param {string} [props.hint]
 */
export default function SignalKCatalogPicker({
  vesselId,
  selectionMode = 'single',
  dark = false,
  selectedPaths = [],
  onPathSelect,
  footerExtra = null,
  hint = '',
}) {
  const pickOne = selectionMode === 'single';
  const [paths, setPaths] = useState([]);
  const [counts, setCounts] = useState({ live: 0, stale: 0, archived: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');
  const [hideNoise, setHideNoise] = useState(true);
  const [selected, setSelected] = useState(() => new Set(selectedPaths));
  const [expandedCategories, setExpandedCategories] = useState(() => new Set(['electrical']));
  const [expandedCollections, setExpandedCollections] = useState(() => new Set());
  const [electricalExpanded, setElectricalExpanded] = useState(false);

  useEffect(() => {
    setSelected(new Set(selectedPaths.filter(Boolean)));
  }, [selectedPaths.join('|')]);

  const loadCatalog = useCallback(async () => {
    const vid = parseInt(vesselId, 10);
    if (!vid) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: statusFilter, limit: '10000' });
      if (categoryFilter) params.set('category', categoryFilter);
      if (search.trim()) params.set('search', search.trim());
      const catalogRes = await api.get(`/api/vessels/${vid}/signalk/catalog?${params}`);
      setPaths(catalogRes.data.paths || []);
      setCounts(catalogRes.data.counts || { live: 0, stale: 0, archived: 0, total: 0 });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to load path catalog');
      setPaths([]);
    } finally {
      setLoading(false);
    }
  }, [vesselId, statusFilter, categoryFilter, search]);

  useEffect(() => {
    loadCatalog();
    const interval = setInterval(loadCatalog, 30000);
    return () => clearInterval(interval);
  }, [loadCatalog]);

  const categories = useMemo(() => {
    const set = new Set(paths.map((p) => p.category).filter(Boolean));
    return [...set].sort();
  }, [paths]);

  const visiblePaths = useMemo(
    () => paths.filter((p) => !hideNoise || !EXPLORER_HIDDEN_CATEGORIES.has(p.category)),
    [paths, hideNoise]
  );

  const grouped = useMemo(() => groupCatalogForExplorer(visiblePaths), [visiblePaths]);
  const isSearching = Boolean(search.trim());

  useEffect(() => {
    if (electricalExpanded || isSearching || grouped.length === 0) return;
    const electrical = grouped.find((g) => g.category === 'electrical');
    if (!electrical) return;
    setExpandedCategories((prev) => new Set([...prev, 'electrical']));
    setExpandedCollections((prev) => {
      const next = new Set(prev);
      electrical.collections.forEach((c) => next.add(c.key));
      return next;
    });
    setElectricalExpanded(true);
  }, [grouped, isSearching, electricalExpanded]);

  useEffect(() => {
    if (!isSearching || grouped.length === 0) return;
    setExpandedCategories(new Set(grouped.map((g) => g.category)));
    setExpandedCollections(new Set(grouped.flatMap((g) => g.collections.map((c) => c.key))));
  }, [isSearching, grouped]);

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const toggleCollection = (key) => {
    setExpandedCollections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const togglePaths = (pathRows, checked) => {
    if (pickOne) return;
    setSelected((prev) => {
      const next = new Set(prev);
      for (const row of pathRows) {
        if (checked) next.add(row.path);
        else next.delete(row.path);
      }
      return next;
    });
  };

  const pickPath = (path, row) => {
    if (!path) return;
    setSelected(new Set([path]));
    onPathSelect?.(path, row);
  };

  const togglePath = (path) => {
    if (pickOne) {
      pickPath(path);
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const allVisibleSelected =
    !pickOne && visiblePaths.length > 0 && visiblePaths.every((p) => selected.has(p.path));

  const toggleAll = () => {
    if (pickOne) return;
    togglePaths(visiblePaths, !allVisibleSelected);
  };

  const applySelected = () => {
    const first = [...selected][0];
    if (!first) {
      toast.error('Select a path first');
      return;
    }
    if (pickOne) {
      const row = paths.find((p) => p.path === first);
      pickPath(first, row);
    }
  };

  return (
    <Shell>
      {hint ? <Hint $dark={dark}>{hint}</Hint> : null}
      <Toolbar>
        <SearchWrap>
          <SearchIcon $dark={dark} />
          <SearchInput
            $dark={dark}
            placeholder="Search paths (e.g. inverter, batteries.279)…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadCatalog()}
          />
        </SearchWrap>
        <Select $dark={dark} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="live">Live</option>
          <option value="stale">Stale</option>
        </Select>
        <Select $dark={dark} value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.35rem',
            fontSize: theme.fontSizes.sm,
            color: dark ? '#cbd5e1' : theme.colors.text.secondary,
          }}
        >
          <Checkbox type="checkbox" checked={hideNoise} onChange={(e) => setHideNoise(e.target.checked)} />
          Hide noise
        </label>
        <Btn type="button" $dark={dark} onClick={loadCatalog}>
          Refresh
        </Btn>
      </Toolbar>

      <Panel $dark={dark}>
        <ColumnHeader $dark={dark} $pickOne={pickOne}>
          {!pickOne ? (
            <Checkbox type="checkbox" checked={allVisibleSelected} onChange={toggleAll} />
          ) : null}
          <span>Reading</span>
          <span>Value</span>
          <span>Status</span>
          <span>Updated</span>
          {pickOne ? <span /> : null}
        </ColumnHeader>

        <ListWrap>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: dark ? '#94a3b8' : undefined }}>
              Loading catalog…
            </div>
          ) : grouped.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: dark ? '#94a3b8' : undefined }}>
              No paths match your filters.
            </div>
          ) : (
            grouped.map((cat) => {
              const catExpanded = expandedCategories.has(cat.category);
              const catPaths = cat.collections.flatMap((c) => c.paths);
              const catSel = selectionState(catPaths, selected);

              return (
                <CategoryBlock key={cat.category} $dark={dark}>
                  <CategoryHeader type="button" $dark={dark} onClick={() => toggleCategory(cat.category)}>
                    {catExpanded ? (
                      <ChevronDownIcon className="w-4 h-4" />
                    ) : (
                      <ChevronRightIcon className="w-4 h-4" />
                    )}
                    {!pickOne ? (
                      <Checkbox
                        type="checkbox"
                        checked={catSel.all}
                        ref={(el) => {
                          if (el) el.indeterminate = catSel.some;
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => togglePaths(catPaths, e.target.checked)}
                      />
                    ) : null}
                    <span>{cat.label}</span>
                    <CountBadge $dark={dark}>
                      {cat.pathCount} paths
                      {catSel.count > 0 ? ` · ${catSel.count} selected` : ''}
                    </CountBadge>
                  </CategoryHeader>

                  {catExpanded &&
                    cat.collections.map((coll) => {
                      const collExpanded = expandedCollections.has(coll.key);
                      const collSel = selectionState(coll.paths, selected);

                      return (
                        <div key={coll.key}>
                          <CollectionHeader type="button" $dark={dark} onClick={() => toggleCollection(coll.key)}>
                            {collExpanded ? (
                              <ChevronDownIcon className="w-4 h-4" />
                            ) : (
                              <ChevronRightIcon className="w-4 h-4" />
                            )}
                            {!pickOne ? (
                              <Checkbox
                                type="checkbox"
                                checked={collSel.all}
                                ref={(el) => {
                                  if (el) el.indeterminate = collSel.some;
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => togglePaths(coll.paths, e.target.checked)}
                              />
                            ) : null}
                            <span>{coll.label}</span>
                            <CountBadge $dark={dark}>{coll.paths.length} paths</CountBadge>
                          </CollectionHeader>

                          {collExpanded &&
                            coll.paths.map((row) => {
                              const suffix = catalogPathSuffix(row.path, coll.key);
                              const reading = formatExplorerReading(row.path, row.value, row.units);
                              const valueText = formatExplorerDisplayString(
                                row.path,
                                row.value,
                                row.units
                              );
                              const isSelected = selected.has(row.path);

                              return (
                                <PathRow
                                  key={row.path}
                                  $dark={dark}
                                  $pickOne={pickOne}
                                  style={
                                    isSelected
                                      ? { outline: `2px solid ${theme.colors.primary.main}`, outlineOffset: -2 }
                                      : undefined
                                  }
                                >
                                  {!pickOne ? (
                                    <Checkbox
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => togglePath(row.path)}
                                    />
                                  ) : null}
                                  <div title={row.path}>
                                    <ReadingLabel $dark={dark}>{reading.label}</ReadingLabel>
                                    <ReadingSub $dark={dark}>{suffix}</ReadingSub>
                                  </div>
                                  <ValueCell $dark={dark}>{valueText}</ValueCell>
                                  <span style={{ fontSize: theme.fontSizes.xs, color: dark ? '#94a3b8' : undefined }}>
                                    <StatusDot $color={statusColor(row.status, theme)} />
                                    {statusLabel(row.status)}
                                  </span>
                                  <span style={{ fontSize: theme.fontSizes.xs, color: dark ? '#94a3b8' : undefined }}>
                                    {formatTs(row.lastSeen)}
                                  </span>
                                  {pickOne ? (
                                    <UsePathButton type="button" onClick={() => pickPath(row.path, row)}>
                                      Use path
                                    </UsePathButton>
                                  ) : null}
                                </PathRow>
                              );
                            })}
                        </div>
                      );
                    })}
                </CategoryBlock>
              );
            })
          )}
        </ListWrap>

        <Footer $dark={dark}>
          <span>
            {counts.total} paths ({counts.live} live, {counts.stale} stale)
            {pickOne && selected.size ? ` · selected: ${[...selected][0]}` : ''}
          </span>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {footerExtra}
            {pickOne ? (
              <Btn type="button" $primary $dark={dark} disabled={!selected.size} onClick={applySelected}>
                Use selected path
              </Btn>
            ) : null}
          </div>
        </Footer>
      </Panel>
    </Shell>
  );
}
